import { useEffect, useCallback, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuthStore } from './stores/authStore'
import { postLoginDestination } from './utils/postLoginDestination'
import { CookieConsent } from './components/CookieConsent'
import RouteSEO from './components/RouteSEO'
import { captureReferralFromUrl } from './utils/referralTracking'
import { readAuthReturn } from './utils/authReturn'
import { useInactivityTimeout } from './hooks/useInactivityTimeout'
import { usePageTracking } from './hooks/usePageTracking'
import { useCartSync } from './hooks/useCartSync'

// Admin / account / reseller screens load on demand (owner 2026-10-15): the public site used to
// ship every screen of the system in one 3.8 MB file, slow on phones. After a deploy the old
// chunk names are gone, so a stale tab reloads ONCE to pick up the new build instead of crashing.
function lazyPage<T extends React.ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() => load().then((m) => {
    try { sessionStorage.removeItem('chunk-reload') } catch { /* storage blocked */ }
    return m
  }).catch((err) => {
    let reloaded = false
    try { reloaded = sessionStorage.getItem('chunk-reload') === '1'; sessionStorage.setItem('chunk-reload', '1') } catch { /* */ }
    if (!reloaded) { window.location.reload(); return new Promise<{ default: T }>(() => {}) }
    throw err
  }))
}

const PageLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
  </div>
)

// Portal Context
import { PortalProvider, usePortal } from './contexts/PortalContext'
import PortalLayout from './layouts/PortalLayout'
import ScrollToTop from './components/ScrollToTop'
import NewVersionPrompt from './components/NewVersionPrompt'
import PortalHome from './pages/portal/PortalHome'
import PortalRegister from './pages/portal/PortalRegister'
const PortalResellerDashboard = lazyPage(() => import('./pages/portal/ResellerDashboard'))
const PortalManagement = lazyPage(() => import('./pages/admin/PortalManagement'))

// Layouts
import WebsiteLayout from './layouts/WebsiteLayout'
const AdminLayout = lazyPage(() => import('./layouts/AdminLayout'))
const AccountLayout = lazyPage(() => import('./layouts/AccountLayout'))
const ViewAsCustomer = lazyPage(() => import('./pages/ViewAsCustomer'))

// Website Pages
import Home from './pages/website/Home'
import { Register } from './pages/website/Register'
import { Login } from './pages/website/Login'
import { Verify } from './pages/website/Verify'
import Shop from './pages/website/Shop'
import ProductDetail from './pages/website/ProductDetail'
import Cart from './pages/website/Cart'
import Checkout from './pages/website/Checkout'
import BasketRestore from './pages/website/BasketRestore'
import PaypalReturn from './pages/website/PaypalReturn'
import PaymentSuccess from './pages/website/PaymentSuccess'
import Unsubscribe from './pages/website/Unsubscribe'
import PayLink from './pages/website/PayLink'
import OrderConfirmation from './pages/website/OrderConfirmation'
import FAQ from './pages/website/FAQ'
import Contact from './pages/website/Contact'
import Terms from './pages/website/Terms'
import Privacy from './pages/website/Privacy'
import FreeSimTerms from './pages/website/FreeSimTerms'
import AcceptableUse from './pages/website/AcceptableUse'
import RepairsReplacements from './pages/website/RepairsReplacements'
import About from './pages/website/About'
import ForgotPassword from './pages/website/ForgotPassword'
import FirstTimeLogin from './pages/website/FirstTimeLogin'
import ResetPassword from './pages/website/ResetPassword'
import CompleteProfile from './pages/website/CompleteProfile'
import SpinWheel from './pages/website/SpinWheel'
// /free-sim + /free-sim-card are 301'd to /sim-only (owner 2026-10-11: forward the free SIM SEO
// to the paid SIM pages). The page stays in pages/website/FreeSIM.tsx.
import VoIP from './pages/website/VoIP'
import NetworkCoverage from './pages/website/NetworkCoverage'
import Roaming from './pages/website/Roaming'
import ThreeGoRoamRates from './pages/website/ThreeGoRoamRates'
import ThreeGoRoamDestinations from './pages/website/ThreeGoRoamDestinations'
import StarlinkBroadband from './pages/website/StarlinkBroadband'
import BroadbandGuide from './pages/website/BroadbandGuide'
import ResellerProgram from './pages/website/ResellerProgram'
import SpecialOffers from './pages/website/SpecialOffers'
import SignContract from './pages/website/SignContract'
import SetupDirectDebit from './pages/website/SetupDirectDebit'
import SetupCard from './pages/website/SetupCard'
import WebsiteTerms from './pages/website/WebsiteTerms'
import ComplaintsCode from './pages/website/ComplaintsCode'
import CookiePolicy from './pages/website/CookiePolicy'
import EarlyTermination from './pages/website/EarlyTermination'
// SEO Landing Pages
import CheapSimCards from './pages/website/CheapSimCards'
import DataSimCards from './pages/website/DataSimCards'
import CheapBroadband from './pages/website/CheapBroadband'
import MobileBroadband from './pages/website/MobileBroadband'
import NoContractSim from './pages/website/NoContractSim'
import SimOnlyDeals from './pages/website/SimOnlyDeals'
// GEO Landing Pages
import BroadbandCaravan from './pages/website/BroadbandCaravan'
import GeoSimCards from './pages/website/GeoSimCards'
import GeoBroadband from './pages/website/GeoBroadband'
import GeoLanding from './pages/website/GeoLanding'
import BroadbandRural from './pages/website/BroadbandRural'
import BroadbandRenters from './pages/website/BroadbandRenters'
import BroadbandHolidayHome from './pages/website/BroadbandHolidayHome'
import RollingMonthlyBroadband from './pages/website/RollingMonthlyBroadband'
// Blog
import Blog from './pages/website/Blog'
import BlogPost from './pages/website/BlogPost'

// Admin Pages
const AdminDashboard = lazyPage(() => import('./pages/admin/Dashboard'))
const AdminOrders = lazyPage(() => import('./pages/admin/Orders'))
const AdminCustomers = lazyPage(() => import('./pages/admin/Customers'))
const AdminReplacements = lazyPage(() => import('./pages/admin/Replacements'))
const AdminPayGoMonitor = lazyPage(() => import('./pages/admin/PayGoMonitor'))
const AdminNetworkOutages = lazyPage(() => import('./pages/admin/NetworkOutages'))
const AdminCancellations = lazyPage(() => import('./pages/admin/Cancellations'))
const AdminDebitOrderUpdates = lazyPage(() => import('./pages/admin/DebitOrderUpdates'))
const LegacyWallet = lazyPage(() => import('./pages/admin/LegacyWallet'))
const AdminProducts = lazyPage(() => import('./pages/admin/Products'))
const AdminStock = lazyPage(() => import('./pages/admin/Stock'))
const AdminResellerPayGo = lazyPage(() => import('./pages/admin/ResellerPayGo'))
const AccountLinkPayGoSim = lazyPage(() => import('./pages/account/LinkPayGoSim'))
const AdminTickets = lazyPage(() => import('./pages/admin/Tickets'))
const AdminReports = lazyPage(() => import('./pages/admin/Reports'))
const AdminSettings = lazyPage(() => import('./pages/admin/Settings'))
const AdminResellerManagement = lazyPage(() => import('./pages/admin/ResellerManagement'))
const AdminEmailTemplates = lazyPage(() => import('./pages/admin/EmailTemplates'))
const AdminSMSTemplates = lazyPage(() => import('./pages/admin/SMSTemplates'))
const AdminAPISettings = lazyPage(() => import('./pages/admin/APISettings'))
const AdminSIMOperations = lazyPage(() => import('./pages/admin/SIMOperations'))
const AdminRoyalMailTracking = lazyPage(() => import('./pages/admin/TrackingSettings'))
const AdminCronMonitor = lazyPage(() => import('./pages/admin/CronMonitor'))
const AdminCronEmailPreviews = lazyPage(() => import('./pages/admin/CronEmailPreviews'))
const AdminSmsDelivery = lazyPage(() => import('./pages/admin/SmsDeliveryReports'))
const AdminSMSQueue = lazyPage(() => import('./pages/admin/SMSQueue'))
const AdminActivityLogs = lazyPage(() => import('./pages/admin/ActivityLogs'))
const AdminLegalDocuments = lazyPage(() => import('./pages/admin/LegalDocuments'))
const AdminContractTemplates = lazyPage(() => import('./pages/admin/ContractTemplates'))
const AdminMarketingDashboard = lazyPage(() => import('./pages/admin/marketing/MarketingDashboard'))
const AdminEmailMarketing = lazyPage(() => import('./pages/admin/marketing/EmailMarketing'))
const AdminSMSMarketing = lazyPage(() => import('./pages/admin/marketing/SMSMarketing'))
const AdminUnsubscribeLists = lazyPage(() => import('./pages/admin/marketing/UnsubscribeLists'))
const AdminDuplicateContacts = lazyPage(() => import('./pages/admin/marketing/DuplicateContacts'))
const AdminWhatsAppMarketing = lazyPage(() => import('./pages/admin/marketing/WhatsAppMarketing'))
const AdminContactLists = lazyPage(() => import('./pages/admin/marketing/ContactLists'))
const AdminReviewRequests = lazyPage(() => import('./pages/admin/marketing/ReviewRequests'))
const AdminSEO = lazyPage(() => import('./pages/admin/SEO'))
const AdminBlog = lazyPage(() => import('./pages/admin/BlogManagement'))
const AdminRankTracker = lazyPage(() => import('./pages/admin/RankTracker'))
const AdminVisitorAnalytics = lazyPage(() => import('./pages/admin/VisitorAnalytics'))
const SalesScorecard = lazyPage(() => import('./pages/admin/sales/Scorecard'))
const SalesFollowups = lazyPage(() => import('./pages/admin/sales/Followups'))
const SalesQueue = lazyPage(() => import('./pages/admin/sales/Queue'))
const SalesRequests = lazyPage(() => import('./pages/admin/sales/Requests'))
const SalesSettings = lazyPage(() => import('./pages/admin/sales/Settings'))
const DataUsage = lazyPage(() => import('./pages/admin/DataUsage'))
const AccountRenew = lazyPage(() => import('./pages/account/Renew'))
const AdminNewsletter = lazyPage(() => import('./pages/admin/Newsletter'))
const AdminReferrals = lazyPage(() => import('./pages/admin/Referrals'))
const AdminFAQ = lazyPage(() => import('./pages/admin/FAQ'))
const AdminDiscounts = lazyPage(() => import('./pages/admin/Discounts'))
const AdminSpinWheel = lazyPage(() => import('./pages/admin/SpinWheel'))
const AdminBackup = lazyPage(() => import('./pages/admin/Backup'))
const AdminMigration = lazyPage(() => import('./pages/admin/Migration'))
const AdminFinancialOverview = lazyPage(() => import('./pages/admin/FinancialOverview'))
const AdminDebitOrders = lazyPage(() => import('./pages/admin/DebitOrders'))
const AdminPayments = lazyPage(() => import('./pages/admin/payments/Payments'))

// Account Pages
const AccountDashboard = lazyPage(() => import('./pages/account/Dashboard'))
const AccountInvoices = lazyPage(() => import('./pages/account/Invoices'))
const AccountStatements = lazyPage(() => import('./pages/account/Statements'))
const AccountContracts = lazyPage(() => import('./pages/account/Contracts'))
const AccountPayments = lazyPage(() => import('./pages/account/Payments'))
const AccountOrders = lazyPage(() => import('./pages/account/Orders'))
const AccountProfile = lazyPage(() => import('./pages/account/Profile'))
const AccountSupport = lazyPage(() => import('./pages/account/Support'))
// AccountNetworkRefresh removed - redirected to SimSettings
const AccountSimSettings = lazyPage(() => import('./pages/account/SimFeatures'))
const AccountTopUp = lazyPage(() => import('./pages/account/TopUp'))
const AccountSimActivation = lazyPage(() => import('./pages/account/SimActivation'))
const AccountSimReplacement = lazyPage(() => import('./pages/account/SimReplacement'))
const AccountCancelContract = lazyPage(() => import('./pages/account/CancelContract'))
const AccountChangeDebitOrder = lazyPage(() => import('./pages/account/ChangeDebitOrder'))
const AccountPACTransfer = lazyPage(() => import('./pages/account/PACTransfer'))
const AccountNetworkCoverage = lazyPage(() => import('./pages/account/NetworkCoverage'))
const AccountNetworkStatus = lazyPage(() => import('./pages/account/NetworkStatus'))
const AccountNewsletter = lazyPage(() => import('./pages/account/Newsletter'))
const AccountReferralProgram = lazyPage(() => import('./pages/account/ReferralProgram'))

// Reseller Pages
const ResellerDashboard = lazyPage(() => import('./pages/reseller/Dashboard'))
const WholesaleDashboard = lazyPage(() => import('./pages/wholesale/WholesaleDashboard'))
const AdminWholesalers = lazyPage(() => import('./pages/admin/Wholesalers'))
const ResellerLogin = lazyPage(() => import('./pages/reseller/ResellerLogin'))

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

// Fires public website page-view tracking. Renders nothing. Must live inside
// the Router so the route-aware tracking hook can read the current location.
function PageTracker() {
  usePageTracking()
  return null
}

/**
 * The customer dashboard's child routes, defined ONCE and rendered by both the main site
 * and every white-label portal.
 *
 * They used to live only in the main route tree while a portal had a bare
 * `/account/*` -> <AccountLayout />. AccountLayout is just chrome around an <Outlet />, so
 * on a reseller domain every dashboard page — top-up, payments, invoices, SIM settings —
 * rendered the menu with an empty panel. Sharing the list means a portal customer's
 * dashboard mirrors the original exactly, and a page added here can never again reach one
 * site and not the other.
 */
const accountChildRoutes = ({ isPortal = false }: { isPortal?: boolean } = {}) => (
  <>
    <Route index element={<AccountDashboard />} />
    <Route path="orders" element={<AccountOrders />} />
    <Route path="invoices" element={<AccountInvoices />} />
    <Route path="statements" element={<AccountStatements />} />
    <Route path="contracts" element={<AccountContracts />} />
    <Route path="payments" element={<AccountPayments />} />
    <Route path="profile" element={<AccountProfile />} />
    {/* Support tickets are main-site only. We cannot answer a ticket on a reseller's domain
        without disclosing Silux Mobile behind their brand, so a portal gets no ticket page —
        by URL either, not just by a hidden menu item. Withheld here rather than deleted
        because the main site still needs it. Any portal created later inherits this.

        On a portal the path still resolves, as a redirect to the dashboard: FAQ and the
        account tour link to it, and an unmatched /account/* path on a portal falls through
        to the storefront home page, which would dump a signed-in customer out of their
        dashboard with no explanation. */}
    {isPortal
      ? <Route path="support" element={<Navigate to="/account" replace />} />
      : <Route path="support" element={<AccountSupport />} />}
    <Route path="network-refresh" element={<Navigate to="/account/sim-settings" replace />} />
    <Route path="sim-settings" element={<AccountSimSettings />} />
    <Route path="topup" element={<AccountTopUp />} />
    <Route path="link-paygo-sim" element={<AccountLinkPayGoSim />} />
    <Route path="sim-activation" element={<AccountSimActivation />} />
    <Route path="sim-replacement" element={<AccountSimReplacement />} />
    <Route path="cancel-contract" element={<AccountCancelContract />} />
    <Route path="change-debit-order" element={<AccountChangeDebitOrder />} />
    <Route path="pac-transfer" element={<AccountPACTransfer />} />
    <Route path="network-coverage" element={<AccountNetworkCoverage />} />
    <Route path="network-status" element={<AccountNetworkStatus />} />
    <Route path="newsletter" element={<AccountNewsletter />} />
    <Route path="referral-program" element={<AccountReferralProgram />} />
    {/* Renew a contract / move Pay&Go to a monthly plan online (Sales Strategy, main site only). */}
    {!isPortal && <Route path="renew" element={<AccountRenew />} />}
  </>
)

// Portal-aware inner router
function AppRouter() {
  const { isPortal, isLoading } = usePortal()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (isPortal) {
    return (
      <Routes>
        <Route element={<PortalLayout />}>
          <Route index element={<PortalHome />} />
          <Route path="/shop" element={<Shop />} />
          {/* Category routes — the Shop filters by path; portal scoping limits to the portal's products */}
          <Route path="/sim-only" element={<Shop />} />
          <Route path="/sim-only/data-only" element={<Shop />} />
          <Route path="/sim-only/calls-text-data" element={<Shop />} />
          <Route path="/sim-only/multisim" element={<Shop />} />
          <Route path="/sim-only/roaming" element={<Shop />} />
          <Route path="/sim-only/multisim-3net" element={<Shop />} />
          <Route path="/sim-only/multisim-4net" element={<Shop />} />
          <Route path="/sim-only/guarddata-sim" element={<Shop />} />
          <Route path="/sim-only/longlife-sim" element={<Shop />} />
          <Route path="/broadband" element={<Shop />} />
          <Route path="/broadband/premium" element={<Shop />} />
          <Route path="/broadband/lite" element={<Shop />} />
          <Route path="/broadband/mifi" element={<Shop />} />
          <Route path="/bundles" element={<Shop />} />

          {/* The buying journey. Every one of these was missing, and because an unmatched
              path falls through to <PortalHome /> the failure was silent: a portal customer
              could fill a basket and never reach a checkout. Same components as the main
              site — the portal is a different origin, not a different shop, and keeping
              checkout ON the portal domain is what preserves the reseller's commission
              (Order.portal_id comes from the X-Portal-Subdomain header nginx sets for
              *.mobileflow.uk) along with the cart, the session and the branding. */}
          <Route path="/product/:productKey" element={<ProductDetail />} />
          <Route path="/product/:productKey/:legacySlug" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/paypal-return" element={<PaypalReturn />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />

          {/* Sign-in and the rest of the auth journey */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<PortalRegister />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/first-time-login" element={<FirstTimeLogin />} />
          {/* Sign-in sends a customer here when their profile is incomplete. Without it they
              bounced to the home page and could never finish signing up. */}
          <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
          {/* Contract and Direct Debit signing. The emailed links point at the main domain,
              so these are a safety net: a link opened on the reseller's domain works instead
              of dumping the customer on the home page. */}
          <Route path="/sign-contract/:token" element={<SignContract />} />
          <Route path="/setup-direct-debit/:token" element={<SetupDirectDebit />} />
          {/* Card-on-file mandate — the card twin of the Direct Debit link above. */}
          <Route path="/setup-card/:token" element={<SetupCard />} />

          {/* The customer dashboard — the SAME children as the main site, so top-up and
              account payments work here too, behind the same auth guard. */}
          <Route path="/account" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}>
            {accountChildRoutes({ isPortal: true })}
          </Route>

          {/* Coverage checker — mirrored from the main site, with its own page and a postcode
              box on the portal home page. */}
          <Route path="/network-coverage" element={<NetworkCoverage />} />

          {/* Support pages */}
          <Route path="/faq" element={<FAQ />} />

          {/* EVERY legal document is published on every white-label site: a customer must be
              able to read the terms they are agreeing to on the site they are buying from.
              PortalLayout adds the line naming the reseller as a representative partner of
              Silux Mobile, who remains the contracting party. */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          <Route path="/website-terms" element={<WebsiteTerms />} />
          <Route path="/complaints-code" element={<ComplaintsCode />} />
          <Route path="/early-termination" element={<EarlyTermination />} />
          <Route path="/repairs-replacements" element={<RepairsReplacements />} />
          <Route path="/free-sim-terms" element={<FreeSimTerms />} />

          <Route path="/portal-dashboard" element={<PortalResellerDashboard />} />
          <Route path="*" element={<PortalHome />} />
        </Route>
      </Routes>
    )
  }

  return <MainAppRoutes />
}

// Protected Route Components
const ProtectedRoute = ({ children, adminOnly = false }: { children: React.ReactNode; adminOnly?: boolean }) => {
  const { isAuthenticated, user } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (adminOnly && !user?.is_admin) {
    return <Navigate to="/account" replace />
  }

  return <>{children}</>
}

// Reseller/affiliate gate — sends unauthenticated users to the ISOLATED reseller login
// (not the main platform login), so the affiliate area stays self-contained.
const ResellerRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthStore()
  if (!isAuthenticated) {
    return <Navigate to="/reseller/login" replace />
  }
  return <>{children}</>
}

const GuestRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user } = useAuthStore()

  if (isAuthenticated) {
    // Honour a pending post-login destination (e.g. sign-in FROM checkout returns you there,
    // not the dashboard). This declarative redirect always wins over the login page's own
    // navigate, so IT must read the destination — and from sessionStorage, not the URL, because
    // the login page's navigate mutates the URL first (which previously made this read empty and
    // bounce to /account). Internal same-origin paths only; consumed once.
    // Read-only (no clear here): clearing during render can double-read to empty and bounce to
    // the dashboard. The destination (checkout) consumes/clears the flag when it mounts. A
    // stale/abandoned flag is auto-ignored by readAuthReturn()'s TTL.
    const returnTo = readAuthReturn()
    // Role-aware, and shared with the login page. Previously this sent every non-admin to
    // /account, which silently overrode the login page's own routing — a wholesaler or reseller
    // never reached their dashboard.
    return <Navigate to={returnTo || postLoginDestination(user)} replace />
  }

  return <>{children}</>
}

function MainAppRoutes() {
  const { user, isAuthenticated, logout } = useAuthStore()

  // Capture referral code from URL on any page load
  useEffect(() => {
    captureReferralFromUrl()
  }, [])

  // Keep the cart synced to the customer profile so it follows them across devices.
  useCartSync()

  // Global inactivity timeout — applies to web dashboard, admin panel, and all
  // authenticated sessions. Timeout values come from Admin → System Settings →
  // Login Timeouts. No mouse/keyboard/touch/scroll for the configured duration
  // triggers an automatic logout.
  const handleTimeout = useCallback(() => {
    logout()
    // Brief delay so the logout completes before the page reloads to /login
    setTimeout(() => {
      window.location.href = '/login?reason=timeout'
    }, 100)
  }, [logout])

  useInactivityTimeout(isAuthenticated, user?.is_admin ?? false, handleTimeout)

  // Sync admin status to the external chat widget so it skips auto-open for admins
  useEffect(() => {
    const isAdmin = user?.is_admin === true
    if ((window as any).AI_CHATBOT_CONFIG) {
      (window as any).AI_CHATBOT_CONFIG.isAdmin = isAdmin
    } else {
      (window as any).AI_CHATBOT_CONFIG = { isAdmin }
    }
  }, [user])

  return (
        <Routes>
          {/* Admin read-only "view as customer" bootstrap (sets per-tab session, then redirects) */}
          <Route path="/view-as" element={<ViewAsCustomer />} />
          {/* Standalone public payment page (no website chrome) */}
          <Route path="/pay/:token" element={<PayLink />} />

          {/* Website Routes */}
          <Route element={<WebsiteLayout />}>
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            {/* productKey = slug (/product/<slug>) or legacy numeric id (/product/162).
                The legacy two-segment /product/<id>/<old-name> form also resolves and
                ProductDetail redirects all of them to the canonical /product/<slug>. */}
            <Route path="/product/:productKey" element={<ProductDetail />} />
            <Route path="/product/:productKey/:legacySlug" element={<ProductDetail />} />
            <Route path="/sim-only" element={<Shop />} />
            <Route path="/sim-only/data-only" element={<Shop />} />
            <Route path="/sim-only/calls-text-data" element={<Shop />} />
            <Route path="/sim-only/multisim" element={<Shop />} />
            <Route path="/sim-only/multisim-3net" element={<Shop />} />
            <Route path="/sim-only/multisim-4net" element={<Shop />} />
            <Route path="/sim-only/roaming" element={<Shop />} />
            <Route path="/sim-only/guarddata-sim" element={<Shop />} />
            <Route path="/sim-only/longlife-sim" element={<Shop />} />
            <Route path="/roaming" element={<Roaming />} />
            <Route path="/roaming/multinet" element={<Roaming />} />
            <Route path="/roaming/zone1" element={<Roaming />} />
            <Route path="/roaming/zone2" element={<Roaming />} />
            <Route path="/roaming/zone3" element={<Roaming />} />
            <Route path="/roaming/zone4" element={<Roaming />} />
            <Route path="/roaming/zone5" element={<Roaming />} />
            <Route path="/roaming/zone6" element={<Roaming />} />
            <Route path="/three-go-roam/rates" element={<ThreeGoRoamRates />} />
            <Route path="/three-go-roam/destinations" element={<ThreeGoRoamDestinations />} />
            <Route path="/broadband" element={<Shop />} />
            <Route path="/broadband/starlink" element={<StarlinkBroadband />} />
            <Route path="/broadband/guide" element={<BroadbandGuide />} />
            <Route path="/broadband/premium" element={<Shop />} />
            <Route path="/broadband/lite" element={<Shop />} />
            <Route path="/broadband/mifi" element={<Shop />} />
            <Route path="/bundles" element={<Shop />} />
            <Route
              path="/register"
              element={
                <GuestRoute>
                  <Register />
                </GuestRoute>
              }
            />
            <Route
              path="/login"
              element={
                <GuestRoute>
                  <Login />
                </GuestRoute>
              }
            />
            <Route path="/verify" element={<Verify />} />
            <Route
              path="/forgot-password"
              element={
                <GuestRoute>
                  <ForgotPassword />
                </GuestRoute>
              }
            />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/first-time-login" element={<FirstTimeLogin />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/basket/restore/:token" element={<BasketRestore />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/checkout/paypal-return" element={<PaypalReturn />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/free-sim-terms" element={<FreeSimTerms />} />
            <Route path="/acceptable-use" element={<AcceptableUse />} />
            <Route path="/repairs-replacements" element={<RepairsReplacements />} />
            <Route path="/website-terms" element={<WebsiteTerms />} />
            <Route path="/complaints-code" element={<ComplaintsCode />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/early-termination" element={<EarlyTermination />} />
            <Route path="/about" element={<About />} />
            <Route path="/free-sim" element={<Navigate to="/sim-only" replace />} />
            <Route path="/voip" element={<VoIP />} />
            <Route path="/network-coverage" element={<NetworkCoverage />} />
            <Route path="/reseller-program" element={<ResellerProgram />} />
            <Route path="/special-offers" element={<SpecialOffers />} />
            {/* SEO Landing Pages */}
            <Route path="/cheap-sim-cards" element={<CheapSimCards />} />
            <Route path="/data-sim-cards" element={<DataSimCards />} />
            <Route path="/data-sim" element={<DataSimCards />} />
            <Route path="/cheap-broadband" element={<CheapBroadband />} />
            <Route path="/mobile-broadband" element={<MobileBroadband />} />
            <Route path="/no-contract-sim" element={<NoContractSim />} />
            <Route path="/sim-only-deals" element={<SimOnlyDeals />} />
            <Route path="/broadband-caravan" element={<BroadbandCaravan />} />
            <Route path="/broadband-rural-uk" element={<BroadbandRural />} />
            <Route path="/broadband-renters" element={<BroadbandRenters />} />
            <Route path="/broadband-holiday-home" element={<BroadbandHolidayHome />} />
            <Route path="/rolling-monthly-broadband" element={<RollingMonthlyBroadband />} />
            <Route path="/cheap-sim-cards/:city" element={<GeoSimCards />} />
            <Route path="/mobile-broadband/:city" element={<GeoBroadband />} />
            {/* GEO city variants (generic engine). Static sub-routes like /sim-only/data-only
                rank above these :city params in React Router v6, so they are unaffected. */}
            <Route path="/data-sim-cards/:city" element={<GeoLanding configKey="data-sim-cards" />} />
            <Route path="/cheap-broadband/:city" element={<GeoLanding configKey="cheap-broadband" />} />
            <Route path="/no-contract-sim/:city" element={<GeoLanding configKey="no-contract-sim" />} />
            <Route path="/sim-only-deals/:city" element={<GeoLanding configKey="sim-only-deals" />} />
            <Route path="/sim-only/:city" element={<GeoLanding configKey="sim-only" />} />
            <Route path="/broadband/:city" element={<GeoLanding configKey="broadband" />} />
            <Route path="/bundles/:city" element={<GeoLanding configKey="bundles" />} />
            <Route path="/broadband-rural-uk/:city" element={<GeoLanding configKey="broadband-rural-uk" />} />
            <Route path="/broadband-caravan/:city" element={<GeoLanding configKey="broadband-caravan" />} />
            <Route path="/broadband-holiday-home/:city" element={<GeoLanding configKey="broadband-holiday-home" />} />
            <Route path="/broadband-renters/:city" element={<GeoLanding configKey="broadband-renters" />} />
            <Route path="/rolling-monthly-broadband/:city" element={<GeoLanding configKey="rolling-monthly-broadband" />} />
            <Route path="/free-sim-card" element={<Navigate to="/sim-only" replace />} />
            {/* Blog */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* Spin & Win — accessible logged in (full) or logged out (teaser) */}
            <Route path="/spin-wheel" element={<SpinWheel />} />
          </Route>

          {/* Complete Profile — post first-login / post password reset */}
          <Route
            path="/complete-profile"
            element={
              <ProtectedRoute>
                <CompleteProfile />
              </ProtectedRoute>
            }
          />

          {/* Contract Signing - Public route with token */}
          <Route path="/sign-contract/:token" element={<SignContract />} />
          <Route path="/setup-direct-debit/:token" element={<SetupDirectDebit />} />
          {/* Card-on-file mandate — the card twin of the Direct Debit link above. */}
          <Route path="/setup-card/:token" element={<SetupCard />} />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="replacements" element={<AdminReplacements />} />
            <Route path="paygo-monitor" element={<AdminPayGoMonitor />} />
            <Route path="network-outages" element={<AdminNetworkOutages />} />
            <Route path="cancellations" element={<AdminCancellations />} />
            {/* New bank details a customer submitted — the inbox for Debit Orders. */}
            <Route path="debit-order-updates" element={<AdminDebitOrderUpdates />} />
            <Route path="legacy-wallet" element={<LegacyWallet />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="stock" element={<AdminStock />} />
            <Route path="reseller-paygo" element={<AdminResellerPayGo />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="email-templates" element={<AdminEmailTemplates />} />
            <Route path="sms-templates" element={<AdminSMSTemplates />} />
            <Route path="api-settings" element={<AdminAPISettings />} />
            <Route path="sim-operations" element={<AdminSIMOperations />} />
            <Route path="royal-mail-tracking" element={<AdminRoyalMailTracking />} />
            <Route path="cron-monitor" element={<AdminCronMonitor />} />
            <Route path="cron-email-previews" element={<AdminCronEmailPreviews />} />
            <Route path="sms-delivery" element={<AdminSmsDelivery />} />
            <Route path="sms-queue" element={<AdminSMSQueue />} />
            <Route path="activity-logs" element={<AdminActivityLogs />} />
            <Route path="legal-documents" element={<AdminLegalDocuments />} />
            <Route path="contract-templates" element={<AdminContractTemplates />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="resellers" element={<AdminResellerManagement />} />
            <Route path="wholesalers" element={<AdminWholesalers />} />
            <Route path="marketing" element={<AdminMarketingDashboard />} />
            <Route path="marketing/email" element={<AdminEmailMarketing />} />
            <Route path="marketing/sms" element={<AdminSMSMarketing />} />
            <Route path="marketing/unsubscribes" element={<AdminUnsubscribeLists />} />
            <Route path="marketing/duplicates" element={<AdminDuplicateContacts />} />
            <Route path="marketing/whatsapp" element={<AdminWhatsAppMarketing />} />
            <Route path="marketing/contact-lists" element={<AdminContactLists />} />
            <Route path="marketing/review-requests" element={<AdminReviewRequests />} />
            <Route path="seo" element={<AdminSEO />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="rank-tracker" element={<AdminRankTracker />} />
            <Route path="visitor-analytics" element={<AdminVisitorAnalytics />} />
            {/* Sales Strategy module (owner 2026-10-15) */}
            <Route path="sales-strategy" element={<Navigate to="/admin/sales-strategy/scorecard" replace />} />
            <Route path="sales-strategy/scorecard" element={<SalesScorecard />} />
            <Route path="sales-strategy/followups" element={<SalesFollowups />} />
            <Route path="sales-strategy/queue" element={<SalesQueue />} />
            <Route path="sales-strategy/requests" element={<SalesRequests />} />
            <Route path="sales-strategy/settings" element={<SalesSettings />} />
            {/* Data Usage monitor (owner 2026-10-27) */}
            <Route path="data-usage" element={<DataUsage tab="sims" />} />
            <Route path="data-usage/steps" element={<DataUsage tab="steps" />} />
            <Route path="data-usage/alerts" element={<Navigate to="/admin/data-usage/steps" replace />} />
            <Route path="data-usage/ignored" element={<DataUsage tab="ignored" />} />
            <Route path="data-usage/not-matched" element={<DataUsage tab="unmatched" />} />
            <Route path="data-usage/settings" element={<DataUsage tab="settings" />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="referrals" element={<AdminReferrals />} />
            <Route path="backup" element={<AdminBackup />} />
            <Route path="migration" element={<AdminMigration />} />
            <Route path="finance" element={<AdminFinancialOverview />} />
            <Route path="debit-orders" element={<AdminDebitOrders />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="faq" element={<AdminFAQ />} />
            <Route path="discounts" element={<AdminDiscounts />} />
            <Route path="spin-wheel" element={<AdminSpinWheel />} />
            <Route path="portals" element={<PortalManagement />} />
          </Route>

          {/* Reseller / Affiliate — isolated login + dashboard (separate from main platform) */}
          <Route path="/reseller/login" element={<ResellerLogin />} />
          <Route
            path="/reseller"
            element={
              <ResellerRoute>
                <ResellerDashboard />
              </ResellerRoute>
            }
          />
          {/* A wholesaler signs in through the same isolated partner login — they are a partner,
              not a customer — but lands on their own dashboard: no storefront, no commission. */}
          <Route
            path="/wholesale"
            element={
              <ResellerRoute>
                <WholesaleDashboard />
              </ResellerRoute>
            }
          />

          {/* Account Routes */}
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountLayout />
              </ProtectedRoute>
            }
          >
            {accountChildRoutes()}
          </Route>

          {/* Catch-all — redirect any unknown URL to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <PortalProvider>
          <PageTracker />
          <RouteSEO />
          {/* Every route change starts at the top — see the component for why. */}
          <ScrollToTop />
          <Suspense fallback={<PageLoading />}>
            <AppRouter />
          </Suspense>
          <CookieConsent />
          {/* Offers a reload when a new build is deployed. Renders nothing until then, and
              stays silent on checkout and payment pages. */}
          <NewVersionPrompt />
        </PortalProvider>
      </Router>
    </QueryClientProvider>
  )
}

export default App
